import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { GlassCard } from '@/components/ui/GlassCard';
import {
  CardHeader,
  CardContent,
  CardTitle,
  CardDescription,
} from '@/components/ui/layout/card';
import { Activity, ExternalLink, Upload, Apple, Heart } from 'lucide-react';
import { initiateStravaConnect, initiateGarminConnect, setAppleHealthConnected, syncAppleHealthActivities } from '@/lib/api';
import type { AppleHealthSyncPayload } from '@/lib/api';
import { isHealthKitPlatform, requestHealthKitAuth, fetchHealthKitWorkouts } from '@/lib/healthkit';
import { toast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/feedback/alert-dialog';
import { useNavigate } from 'react-router-dom';
import { log } from '@/lib/logger';

interface DevicesConnectCardProps {
  onConnect: (provider: 'strava' | 'garmin') => void;
  onSkip: () => void;
}

export function DevicesConnectCard({ onConnect, onSkip }: DevicesConnectCardProps) {
  const [isConnectingStrava, setIsConnectingStrava] = useState(false);
  const [isConnectingGarmin, setIsConnectingGarmin] = useState(false);
  const [isConnectingAppleHealth, setIsConnectingAppleHealth] = useState(false);
  const [appleHealthConnected, setAppleHealthConnectedLocal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const navigate = useNavigate();
  const showAppleHealth = isHealthKitPlatform();

  const handleConnectStrava = async () => {
    setIsConnectingStrava(true);
    try {
      await initiateStravaConnect();
      // Note: We won't reach here because initiateStravaConnect redirects
    } catch (error) {
      setIsConnectingStrava(false);
      log.error('ONBOARDING', 'Failed to connect Strava:', error);
      
      let errorMessage = 'Could not initiate Strava connection';
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      toast({
        title: 'Strava Connection Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  const handleConnectGarmin = async () => {
    setIsConnectingGarmin(true);
    try {
      await initiateGarminConnect();
      // Note: We won't reach here because initiateGarminConnect redirects
    } catch (error) {
      setIsConnectingGarmin(false);
      log.error('ONBOARDING', 'Failed to connect Garmin:', error);
      
      // Check for EMAIL_REQUIRED error
      if (error instanceof Error && error.message === 'EMAIL_REQUIRED') {
        setShowEmailModal(true);
        return;
      }
      
      let errorMessage = 'Could not initiate Garmin connection';
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      toast({
        title: 'Garmin Connection Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  const handleConnectAppleHealth = async () => {
    setIsConnectingAppleHealth(true);
    try {
      const authorized = await requestHealthKitAuth();
      if (!authorized) {
        toast({ title: 'Apple Health Permission Denied', description: 'You can enable it later in Settings → Integrations.', variant: 'destructive' });
        setIsConnectingAppleHealth(false);
        return;
      }
      await setAppleHealthConnected(true);
      setAppleHealthConnectedLocal(true);
      toast({ title: 'Apple Health Connected', description: 'Syncing your recent workouts...' });
      // Sync last 30 days
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const workouts = await fetchHealthKitWorkouts(thirtyDaysAgo, now);
      if (workouts.length > 0) {
        const payload: AppleHealthSyncPayload[] = workouts.map(w => ({
          id: w.id, sport: w.sport, start_date: w.startDate, end_date: w.endDate,
          duration_seconds: w.durationSeconds, distance_meters: w.distanceMeters ?? null,
          calories: w.calories ?? null, avg_heart_rate: w.avgHeartRate ?? null,
          max_heart_rate: w.maxHeartRate ?? null, source_name: w.sourceName ?? null,
          source_bundle_id: w.sourceBundleId ?? null,
        }));
        const result = await syncAppleHealthActivities(payload);
        toast({ title: 'Apple Health Synced', description: `${result.imported} workout${result.imported !== 1 ? 's' : ''} imported.` });
      }
    } catch (error) {
      log.error('ONBOARDING', 'Failed to connect Apple Health:', error);
      toast({ title: 'Apple Health Connection Failed', description: error instanceof Error ? error.message : 'Please try again.', variant: 'destructive' });
    } finally {
      setIsConnectingAppleHealth(false);
    }
  };

  return (
    <>
      <div className="ml-12 mt-4 space-y-3">
        <GlassCard className="border-accent/30 bg-accent/5">
          <CardContent className="p-4 space-y-3">
            {/* Strava - Primary */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-[#FC4C02]/10 flex items-center justify-center shrink-0">
                <Activity className="h-6 w-6 text-[#FC4C02]" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-semibold text-foreground">Connect Strava</h4>
                  <span className="text-xs bg-accent/20 text-accent px-2 py-0.5 rounded">Recommended</span>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  Sync workouts automatically from your devices
                </p>
                <Button 
                  onClick={handleConnectStrava} 
                  disabled={isConnectingStrava || isConnectingGarmin}
                  className="bg-[#FC4C02] hover:bg-[#FC4C02]/90"
                >
                  {isConnectingStrava ? (
                    'Connecting...'
                  ) : (
                    <>
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Connect Strava
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Garmin - Secondary */}
            <div className="flex items-start gap-4 pt-3 border-t border-border/50">
              <div className="w-12 h-12 rounded-lg bg-[#007CC3]/10 flex items-center justify-center shrink-0">
                <img src="/garmin-connect-icon.png" alt="Garmin Connect" className="h-7 w-7 rounded" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-foreground mb-1">Connect Garmin</h4>
                <p className="text-sm text-muted-foreground mb-3">
                  Import workouts and physiological metrics from Garmin devices
                </p>
                <Button 
                  onClick={handleConnectGarmin} 
                  disabled={isConnectingStrava || isConnectingGarmin}
                  variant="outline"
                >
                  {isConnectingGarmin ? (
                    'Connecting...'
                  ) : (
                    <>
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Connect Garmin
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </GlassCard>

        {/* Apple Health — only on iOS */}
        {showAppleHealth && (
          <GlassCard className="border-accent/30 bg-accent/5">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-red-500/10 flex items-center justify-center shrink-0">
                  <Apple className="h-6 w-6 text-red-400" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-foreground">Apple Health</h4>
                    {appleHealthConnected && (
                      <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded">Connected</span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">
                    Import workouts, heart rate, and distance data directly from Apple Health using HealthKit
                  </p>
                  <div className="text-xs text-muted-foreground/70 mb-3 space-y-1.5">
                    <p className="font-medium text-muted-foreground">Data accessed via Apple HealthKit (read-only):</p>
                    <div className="flex flex-wrap gap-1.5">
                      {['Workouts', 'Heart Rate', 'Active Energy', 'Running Distance', 'Cycling Distance'].map((type) => (
                        <span key={type} className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-red-500/10 text-red-400/80 border border-red-500/20">
                          {type}
                        </span>
                      ))}
                    </div>
                    <p className="text-muted-foreground/60">
                      Health data is synced to AthleteSpace to power your training insights. It may be processed by our AI service (OpenAI) for coaching features with your consent. No data is written to Apple Health.
                    </p>
                  </div>
                  {!appleHealthConnected ? (
                    <Button
                      onClick={handleConnectAppleHealth}
                      disabled={isConnectingAppleHealth}
                      variant="outline"
                    >
                      {isConnectingAppleHealth ? (
                        'Connecting...'
                      ) : (
                        <>
                          <Apple className="h-4 w-4 mr-2" />
                          Connect Apple Health
                        </>
                      )}
                    </Button>
                  ) : (
                    <span className="text-xs text-green-400 font-medium">✓ Workouts synced from Apple Health</span>
                  )}
                </div>
              </div>
            </CardContent>
          </GlassCard>
        )}

        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={onSkip} className="text-muted-foreground">
            Skip for now
          </Button>
          <span className="text-xs text-muted-foreground">or</span>
          <Button variant="ghost" size="sm" className="text-muted-foreground">
            <Upload className="h-3.5 w-3.5 mr-1.5" />
            Upload files instead
          </Button>
        </div>
      </div>

      {/* Email Verification Modal */}
      <AlertDialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Email Verification Required</AlertDialogTitle>
            <AlertDialogDescription>
              Please verify your email before connecting Garmin. This ensures we can securely sync your data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => navigate('/settings')}>
              Verify Email
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
